import { AfterViewInit, Component, DestroyRef, effect, ElementRef, HostListener, inject, OnDestroy, output, signal, OutputEmitterRef, ViewChild, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

// models
import {
	AllowedExtensions,
	IChatFileAttachment,
	IChatMessage,
	IChatMessages,
	SearchAttachmentItem,
	SearchItem,
	SearchTabKey,
	TabSearchState,
	VISIBLE_ATTACHMENTS_LIMIT,
	SCROLL_THRESHOLD,
	SEARCH_PAGE_SIZE
} from '../../models/chat';

// services
import { ChatService, IMAGE_EXTENSIONS, VIDEO_EXTENSIONS } from '../../services/chat.service';

// components
import { LoaderComponent } from '../loader/loader.component';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { HumanDatePipe } from '../../pipes/human-date.pipe';
import { MentionParserPipe } from '../../pipes/mention-parser.pipe';
import { SearchHighlightPipe } from '../../pipes/search-highlight.pipe';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';

@Component({
	selector: 'ph-chat-search-panel',
	templateUrl: './ph-chat-search-panel.component.html',
	styleUrl: './ph-chat-search-panel.component.scss',
	imports: [
		MatTooltipModule,
		TranslateModule,
		HumanDatePipe,
		MentionParserPipe,
		PhIconComponent,
		PhIconWrapperComponent,
		LoaderComponent,
		SearchHighlightPipe
	]
})
export class PhChatSearchPanelComponent implements AfterViewInit, OnDestroy {
	private readonly destroyRef: DestroyRef = inject(DestroyRef);
	private readonly chatService: ChatService = inject(ChatService);
	private readonly inputTransitionMs = 240;
	private isClosing = false;

	close: OutputEmitterRef<void> = output<void>();
	navigate: OutputEmitterRef<SearchItem> = output<SearchItem>();

	inputValue: WritableSignal<string> = signal<string>('');
	query: WritableSignal<string> = signal<string>('');
	loading: WritableSignal<boolean> = signal<boolean>(false);
	items: WritableSignal<Array<SearchItem>> = signal<Array<SearchItem>>([]);
	failedThumbKeys: WritableSignal<Set<string>> = signal<Set<string>>(new Set());
	activeIndex: WritableSignal<number> = signal<number>(-1);
	resultsOpen: WritableSignal<boolean> = signal<boolean>(false);
	inputExpanded: WritableSignal<boolean> = signal<boolean>(false);

	private nextMessagesUrl: string | null = null;
	private previousMessagesUrl: string | null = null;
	private isLoadingMore: boolean = false;
	private readonly searchQuery$: Subject<string> = new Subject<string>();
	private currentSearchTab: SearchTabKey = 'messages';
	private readonly tabState: Record<SearchTabKey, TabSearchState> = {
		messages: this.createEmptyState(),
		pinned: this.createEmptyState(),
		media: this.createEmptyState(),
		documents: this.createEmptyState()
	};

	@ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
	@ViewChild('inputWrapper') inputWrapper?: ElementRef<HTMLElement>;
	@ViewChild('resultsWrapper') resultsWrapper?: ElementRef<HTMLElement>;

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent): void {
		if (!this.resultsOpen() || !event?.target) return;

		const target = event.target as HTMLElement;
		const inputEl: HTMLElement | null = this.inputWrapper?.nativeElement ?? null;
		const resultsEl: HTMLElement | null = this.resultsWrapper?.nativeElement ?? null;

		if (inputEl?.contains(target) || resultsEl?.contains(target)) return;
		this.hideResults();
	}

	constructor() {
		effect((): void => {
			const tab: SearchTabKey = this.getTabFromIndex(this.chatService.activeChatTab());
			if (tab === this.currentSearchTab) return;
			this.persistCurrentTabState();
			this.currentSearchTab = tab;
			this.applyCurrentTabState();
			const q: string = (this.query().trim() || this.inputValue().trim());
			if (q.length > 0 && this.query() !== q) {
				this.query.set(q);
				this.chatService.searchPanelQuery.set(q);
			}
			if (q.length > 0 && this.chatService.id != null) {
				setTimeout((): void => {
					if (this.currentSearchTab !== tab) return;
					this.resultsOpen.set(this.shouldOpenResults());
				}, 0);
				this.runSearchForCurrentTab(q, true);
			}
		});

		this.searchQuery$
			.pipe(
				debounceTime(500),
				tap((raw: string): void => {
					const nextQuery: string = (raw ?? '').trim();
					this.query.set(nextQuery);
					this.chatService.searchPanelQuery.set(nextQuery);
				}),
				tap((): void => {
					if (!this.query() || this.chatService.id == null) {
						if (!this.query() && this.isAttachmentTab(this.currentSearchTab)) {
							this.updateAttachmentFilter(this.currentSearchTab, '');
						}
						this.resetState();
					}
				}),
				switchMap((): Observable<{ tab: SearchTabKey; results: Array<SearchItem> }> => {
					const tabAtRequest: SearchTabKey = this.currentSearchTab;
					if (!this.query() || this.chatService.id == null) {
						return of<{ tab: SearchTabKey; results: Array<SearchItem> }>({ tab: tabAtRequest, results: [] });
					}

					this.loading.set(true);

					return this.searchByActiveTab(this.query()).pipe(
						map((results: Array<SearchItem>): { tab: SearchTabKey; results: Array<SearchItem> } => ({
							tab: tabAtRequest,
							results
						})),
						catchError((): Observable<{ tab: SearchTabKey; results: Array<SearchItem> }> => of({
							tab: tabAtRequest,
							results: []
						}))
					);
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((payload: { tab: SearchTabKey; results: Array<SearchItem> }): void => {
				if (payload.tab !== this.currentSearchTab) return;

				const results: Array<SearchItem> = payload.results;
				this.items.set(results);
				this.tabState[this.currentSearchTab].resultsScrollTop = 0;
				this.persistCurrentTabState();
				this.loading.set(false);
				this.setActiveIndex(results.length ? 0 : -1);
				this.resultsOpen.set(this.shouldOpenResults());
			});

		effect((): void => {
			if (!this.isAttachmentTab(this.currentSearchTab)) return;
			this.chatService.searchPanelResultsVisible();
			this.resultsOpen.set(this.shouldOpenResults());
		});
	}

	ngAfterViewInit(): void {
		this.chatService.searchPanelOpen.set(true);
		setTimeout((): void => {
			this.inputExpanded.set(true);
		}, 0);

		setTimeout((): void => {
			this.searchInput?.nativeElement?.focus();
		}, this.inputTransitionMs);
	}

	ngOnDestroy(): void {
		const hadAttachmentSearch: boolean = this.chatService.mediaSearchFilter().trim().length > 0
			|| this.chatService.documentsSearchFilter().trim().length > 0;
		this.chatService.searchPanelOpen.set(false);
		this.chatService.searchPanelQuery.set('');
		this.chatService.mediaSearchFilter.set('');
		this.chatService.documentsSearchFilter.set('');

		if (hadAttachmentSearch) {
			this.chatService.reloadAttachments$.next();
		}
	}

	onInputChange(value: string): void {
		this.inputValue.set(value ?? '');
		this.resultsOpen.set(this.shouldOpenResults(value));
		this.persistCurrentTabState();
		this.searchQuery$.next(value ?? '');
	}

	openResults(): void {
		if (!this.shouldOpenResults()) return;
		this.resultsOpen.set(true);
		setTimeout((): void => this.restoreResultsScroll(), 0);
	}

	hideResults(): void {
		this.persistCurrentTabState();
		this.resultsOpen.set(false);
	}

	onResultsScroll(event: Event): void {
		const target: HTMLElement = event.target as HTMLElement;
		if (!target) return;
		this.persistCurrentTabState();
		if (!this.nextMessagesUrl || this.isLoadingMore) return;
		if (!this.query().trim()) return;

		if (target.scrollTop + target.clientHeight + SCROLL_THRESHOLD >= target.scrollHeight) {
			this.loadMore();
		}
	}

	getThumbKey(item: SearchItem, att: SearchAttachmentItem): string {
		return `${item.id}-${att.file}`;
	}

	markThumbFailed(key: string): void {
		this.failedThumbKeys.update((set: Set<string>): Set<string> => new Set(set).add(key));
	}

	getVisibleAttachments(item: SearchItem): Array<SearchAttachmentItem> {
		const query: string = this.query().toLowerCase();
		const all: Array<SearchAttachmentItem> = item.attachments;
		if (!query) return all.slice(0, VISIBLE_ATTACHMENTS_LIMIT);

		const matched: Array<SearchAttachmentItem> = all.filter((a: SearchAttachmentItem): boolean => a.name.toLowerCase().includes(
			query));
		const rest: Array<SearchAttachmentItem> = all.filter((a: SearchAttachmentItem): boolean => !a.name.toLowerCase().includes(
			query));
		return [...matched, ...rest].slice(0, VISIBLE_ATTACHMENTS_LIMIT);
	}

	getRemainingCount(item: SearchItem): number {
		return Math.max(0, item.attachments.length - VISIBLE_ATTACHMENTS_LIMIT);
	}

	isMediaAttachment(att: SearchAttachmentItem): boolean {
		const ext: AllowedExtensions | null = this.resolveExtension(att);
		return !!ext && (IMAGE_EXTENSIONS.includes(ext) || VIDEO_EXTENSIONS.includes(ext));
	}

	isVideoAttachment(att: SearchAttachmentItem): boolean {
		const ext: AllowedExtensions | null = this.resolveExtension(att);
		return !!ext && VIDEO_EXTENSIONS.includes(ext);
	}

	getFileIcon(att: SearchAttachmentItem): string {
		const ext: string = att.name.split('.').pop()?.toLowerCase() ?? '';
		return this.chatService.getIconForFileExtension(ext);
	}

	private resetState(): void {
		this.items.set([]);
		this.loading.set(false);
		this.setActiveIndex(-1);
		this.resultsOpen.set(false);
		this.nextMessagesUrl = null;
		this.previousMessagesUrl = null;
		this.isLoadingMore = false;
		this.failedThumbKeys.set(new Set());
		this.persistCurrentTabState();
	}

	canNavigate(): boolean {
		return !!this.query().trim() && this.items().length > 0;
	}

	canNavigateUp(): boolean {
		if (!this.canNavigate()) return false;
		const len: number = this.items().length;
		if (len <= 1) return false;
		const index: number = this.activeIndex();
		const normalizedIndex: number = index < 0 || index >= len ? 0 : index;
		return normalizedIndex < len - 1 || !!this.nextMessagesUrl;
	}

	canNavigateDown(): boolean {
		if (!this.canNavigate()) return false;
		const len: number = this.items().length;
		if (len <= 1) return false;
		const index: number = this.activeIndex();
		const normalizedIndex: number = index < 0 || index >= len ? 0 : index;
		return normalizedIndex > 0 || !!this.previousMessagesUrl;
	}

	isLoadingMoreResults(): boolean {
		return this.isLoadingMore;
	}

	setActiveIndex(index: number): void {
		this.activeIndex.set(index);
		this.persistCurrentTabState();
		setTimeout((): void => this.scrollActiveResultIntoView(), 0);
	}

	navigateByDelta(delta: number): void {
		if (!this.canNavigate()) return;
		if (delta > 0 && !this.canNavigateUp()) return;
		if (delta < 0 && !this.canNavigateDown()) return;

		const list: Array<SearchItem> = this.items();
		if (!list.length) return;

		const len: number = list.length;
		const current: number = this.activeIndex();
		const base: number = current < 0 || current >= len ? 0 : current;

		const targetIndex: number = base + delta;

		if (targetIndex >= len) {
			if (!this.nextMessagesUrl) return;

			if (this.isLoadingMore) return;

			const relativeIndex: number = targetIndex - len; 
			this.hideResults();
			const tabAtLoad: SearchTabKey = this.currentSearchTab;
			this.loadMore((addedItems: Array<SearchItem>): void => {
				if (tabAtLoad !== this.currentSearchTab) return;
				const merged: Array<SearchItem> = this.items();
				if (!merged.length) return;
				let newIndex: number;
				const nextItem: SearchItem | undefined = addedItems[relativeIndex];
				if (nextItem) {
					newIndex = merged.findIndex((i: SearchItem): boolean => i.id === nextItem.id);
					if (newIndex < 0) return;
				} else {
					newIndex = merged.length - 1;
				}
				this.setActiveIndex(newIndex);
				this.navigate.emit(merged[newIndex]);
			});
			return;
		}

		if (targetIndex < 0) {
			if (!this.previousMessagesUrl) return;

			if (this.isLoadingMore) return;

			const absoluteDelta: number = Math.abs(targetIndex);
			this.hideResults();
			const tabAtLoad: SearchTabKey = this.currentSearchTab;
			this.loadPrevious((addedItems: Array<SearchItem>): void => {
				if (tabAtLoad !== this.currentSearchTab) return;
				const merged: Array<SearchItem> = this.items();
				if (!merged.length) return;
				let newIndex: number;
				const nextItemIndex: number = addedItems.length - absoluteDelta;
				const nextItem: SearchItem | undefined = addedItems[nextItemIndex];
				if (nextItem) {
					newIndex = merged.findIndex((i: SearchItem): boolean => i.id === nextItem.id);
					if (newIndex < 0) return;
				} else {
					newIndex = 0;
				}
				this.setActiveIndex(newIndex);
				this.navigate.emit(merged[newIndex]);
			});
			return;
		}

		this.setActiveIndex(targetIndex);
		this.navigate.emit(list[targetIndex]);
		this.hideResults();
	}

	onClearOrClose(): void {
		if (!this.inputValue().trim()) {
			this.closeWithAnimation();
			return;
		}

		this.inputValue.set('');
		this.query.set('');
		this.chatService.searchPanelQuery.set('');
		if (this.isAttachmentTab(this.currentSearchTab)) {
			this.updateAttachmentFilter(this.currentSearchTab, '');
		}
		this.resetState();
		this.searchQuery$.next('');
	}

	private closeWithAnimation(): void {
		if (this.isClosing) return;
		this.isClosing = true;
		this.inputExpanded.set(false);

		setTimeout((): void => {
			this.close.emit();
			this.isClosing = false;
		}, this.inputTransitionMs);
	}

	private searchMessages(query: string): Observable<Array<SearchItem>> {
		this.nextMessagesUrl = null;
		this.previousMessagesUrl = null;
		this.isLoadingMore = false;
		this.failedThumbKeys.set(new Set());

		return this.chatService.getMessages(
			this.chatService.id,
			{
				search: query.toLowerCase(),
				deleted: false,
				page_size: SEARCH_PAGE_SIZE
			}
		).pipe(
			catchError((): Observable<IChatMessages> => of({
				results: [],
				next: null,
				previous: null
			} as IChatMessages)),
			tap((page: IChatMessages): void => {
				this.nextMessagesUrl = page.next ?? null;
				this.previousMessagesUrl = page.previous ?? null;
			}),
			map((page: IChatMessages): Array<SearchItem> => this.mapMessages(page))
		);
	}

	private searchByActiveTab(query: string): Observable<Array<SearchItem>> {
		if (this.currentSearchTab === 'pinned') {
			return of(this.searchPinned(query));
		}
		if (this.currentSearchTab === 'media') {
			this.updateAttachmentFilter('media', query);
			return of([]);
		}
		if (this.currentSearchTab === 'documents') {
			this.updateAttachmentFilter('documents', query);
			return of([]);
		}
		return this.searchMessages(query);
	}

	private searchPinned(query: string): Array<SearchItem> {
		const needle: string = query.toLowerCase();
		const pinned: Array<IChatMessage> = this.chatService.pinnedMessages() ?? [];
		return pinned
			.filter((message: IChatMessage): boolean => this.matchesQuery(message, needle))
			.sort((a: IChatMessage, b: IChatMessage): number => {
				const aTime: number = a?.created_at ? new Date(a.created_at).getTime() : 0;
				const bTime: number = b?.created_at ? new Date(b.created_at).getTime() : 0;
				if (aTime !== bTime) return bTime - aTime;
				const aId: number = typeof a.id === 'number' ? a.id : 0;
				const bId: number = typeof b.id === 'number' ? b.id : 0;
				return aId - bId;
			})
			.map((message: IChatMessage): SearchItem => this.mapMessage(message));
	}

	private loadMore(onLoaded?: (addedItems: Array<SearchItem>) => void): void {
		const tabAtLoad: SearchTabKey = this.currentSearchTab;
		if (!this.nextMessagesUrl || this.isLoadingMore) return;
		this.isLoadingMore = true;

		this.chatService
			.loadMessagesPage(this.appendDeletedFilter(this.nextMessagesUrl))
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (page: IChatMessages): void => {
					if (tabAtLoad !== this.currentSearchTab) {
						this.isLoadingMore = false;
						return;
					}
					this.nextMessagesUrl = page.next ?? null;
					this.previousMessagesUrl = page.previous ?? null;

					const addedItems: Array<SearchItem> = this.mapMessages(page);
					this.items.update((current: Array<SearchItem>): Array<SearchItem> => this.mergeUniqueById(
						current,
						addedItems
					));
					this.isLoadingMore = false;
					onLoaded?.(addedItems);
				},
				error: (): void => {
					this.isLoadingMore = false;
				}
			});
	}

	private loadPrevious(onLoaded?: (addedItems: Array<SearchItem>) => void): void {
		const tabAtLoad: SearchTabKey = this.currentSearchTab;
		if (!this.previousMessagesUrl || this.isLoadingMore) return;
		this.isLoadingMore = true;

		this.chatService
			.loadMessagesPage(this.appendDeletedFilter(this.previousMessagesUrl))
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (page: IChatMessages): void => {
					if (tabAtLoad !== this.currentSearchTab) {
						this.isLoadingMore = false;
						return;
					}
					this.nextMessagesUrl = page.next ?? null;
					this.previousMessagesUrl = page.previous ?? null;

					const addedItems: Array<SearchItem> = this.mapMessages(page);
					this.items.update((current: Array<SearchItem>): Array<SearchItem> => this.mergeUniqueById(
						addedItems,
						current
					));
					this.isLoadingMore = false;
					onLoaded?.(addedItems);
				},
				error: (): void => {
					this.isLoadingMore = false;
				}
			});
	}

	private mapMessages(response: IChatMessages): Array<SearchItem> {
		return (response?.results ?? [])
			.filter((m: IChatMessage): boolean => typeof m.id === 'number')
			.map((m: IChatMessage): SearchItem => this.mapMessage(m));
	}

	private mapMessage(message: IChatMessage): SearchItem {
		return {
			id: message.id as number,
			created_at: message.created_at,
			userName: message.user?.full_name ?? message.user?.email ?? '',
			content: message.content ?? '',
			is_pinned: message.is_pinned ?? false,
			attachments: (message.attachments ?? []).map((a: IChatFileAttachment): SearchAttachmentItem => ({
				name: a.name ?? '',
				file: a.file ?? '',
				content_type: (a.content_type ?? null) as AllowedExtensions | null
			}))
		};
	}

	private matchesQuery(message: IChatMessage, query: string): boolean {
		const contentMatch: boolean = (message.content ?? '').toLowerCase().includes(query);
		if (contentMatch) return true;
		return (message.attachments ?? []).some((attachment: IChatFileAttachment): boolean =>
			(attachment.name ?? '').toLowerCase().includes(query)
		);
	}

	private getTabFromIndex(index: number): SearchTabKey {
		if (index === 1) return 'pinned';
		if (index === 2) return 'media';
		if (index === 3) return 'documents';
		return 'messages';
	}

	private createEmptyState(): TabSearchState {
		return {
			items: [],
			activeIndex: -1,
			nextMessagesUrl: null,
			previousMessagesUrl: null,
			failedThumbKeys: new Set<string>(),
			resultsScrollTop: 0
		};
	}

	private persistCurrentTabState(): void {
		this.tabState[this.currentSearchTab] = {
			items: this.items(),
			activeIndex: this.activeIndex(),
			nextMessagesUrl: this.nextMessagesUrl,
			previousMessagesUrl: this.previousMessagesUrl,
			failedThumbKeys: new Set(this.failedThumbKeys()),
			resultsScrollTop: this.resultsWrapper?.nativeElement?.scrollTop ?? this.tabState[this.currentSearchTab].resultsScrollTop
		};
	}

	private applyCurrentTabState(): void {
		const state: TabSearchState = this.tabState[this.currentSearchTab];
		this.items.set(state.items);
		this.activeIndex.set(state.activeIndex);
		this.nextMessagesUrl = state.nextMessagesUrl;
		this.previousMessagesUrl = state.previousMessagesUrl;
		this.failedThumbKeys.set(new Set(state.failedThumbKeys));
		this.loading.set(false);
		this.isLoadingMore = false;
		this.resultsOpen.set(this.shouldOpenResults());
		if (this.isAttachmentTab(this.currentSearchTab)) {
			this.updateAttachmentFilter(this.currentSearchTab, this.query());
		}
		setTimeout((): void => this.restoreResultsScroll(), 0);
	}

	private runSearchForCurrentTab(queryOverride?: string, forceOpen: boolean = false): void {
		const query: string = (queryOverride ?? this.query()).trim();
		if (!query || this.chatService.id == null) return;

		const tabAtRequest: SearchTabKey = this.currentSearchTab;
		if (forceOpen && this.shouldOpenResults(query)) {
			this.resultsOpen.set(true);
		}
		this.loading.set(true);

		this.searchByActiveTab(query)
			.pipe(
				map((results: Array<SearchItem>): { tab: SearchTabKey; results: Array<SearchItem> } => ({
					tab: tabAtRequest,
					results
				})),
				catchError((): Observable<{ tab: SearchTabKey; results: Array<SearchItem> }> => of({
					tab: tabAtRequest,
					results: []
				})),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe((payload: { tab: SearchTabKey; results: Array<SearchItem> }): void => {
				if (payload.tab !== this.currentSearchTab) return;

				this.items.set(payload.results);
				this.tabState[this.currentSearchTab].resultsScrollTop = 0;
				this.persistCurrentTabState();
				this.loading.set(false);
				this.setActiveIndex(payload.results.length ? 0 : -1);
				this.resultsOpen.set(this.shouldOpenResults(query));
			});
	}

	private isAttachmentTab(tab: SearchTabKey): tab is Extract<SearchTabKey, 'media' | 'documents'> {
		return tab === 'media' || tab === 'documents';
	}

	private updateAttachmentFilter(tab: Extract<SearchTabKey, 'media' | 'documents'>, query: string): void {
		const normalizedQuery: string = (query ?? '').trim().toLowerCase();
		const currentQuery: string = (
			tab === 'media'
				? this.chatService.mediaSearchFilter()
				: this.chatService.documentsSearchFilter()
		).trim().toLowerCase();

		if (tab === 'media') {
			this.chatService.mediaSearchFilter.set(normalizedQuery);
		} else {
			this.chatService.documentsSearchFilter.set(normalizedQuery);
		}

		if (currentQuery !== normalizedQuery) {
			this.chatService.reloadAttachments$.next();
		}
	}

	public canShowResults(): boolean {
		if (this.isAttachmentTab(this.currentSearchTab)) {
			return this.chatService.searchPanelResultsVisible();
		}
		return (
			this.chatService.searchPanelOpen()
			&& this.chatService.searchPanelQuery().trim().length > 0
		);
	}

	private shouldOpenResults(queryValue: string = this.query()): boolean {
		return (queryValue ?? '').trim().length > 0 && this.canShowResults();
	}

	private resolveExtension(att: SearchAttachmentItem): AllowedExtensions | null {
		const ext: string | undefined = att.content_type ?? att.name.split('.').pop()?.toLowerCase() as AllowedExtensions;
		return ext && Object.values(AllowedExtensions).includes(ext as AllowedExtensions)
			? ext as AllowedExtensions
			: null;
	}

	private mergeUniqueById(primary: Array<SearchItem>, secondary: Array<SearchItem>): Array<SearchItem> {
		const mapById: Map<number, SearchItem> = new Map();
		for (const item of [...primary, ...secondary]) {
			if (mapById.has(item.id)) continue;
			mapById.set(item.id, item);
		}
		return Array.from(mapById.values());
	}

	/**
	 * Some backends don't propagate filters (like `deleted`) into `next/previous` pagination URLs.
	 * Ensure we keep excluding deleted messages for all pages during search.
	 */
	private appendDeletedFilter(url: string): string {
		if (!url) return url;
		if (url.includes('deleted=')) return url;
		const separator: string = url.includes('?') ? '&' : '?';
		return `${url}${separator}deleted=false`;
	}

	private restoreResultsScroll(): void {
		const resultsElement: HTMLElement | undefined = this.resultsWrapper?.nativeElement;
		if (!resultsElement) return;
		const state: TabSearchState = this.tabState[this.currentSearchTab];
		resultsElement.scrollTop = state?.resultsScrollTop ?? 0;
		this.scrollActiveResultIntoView();
	}

	private scrollActiveResultIntoView(): void {
		const resultsElement: HTMLElement | undefined = this.resultsWrapper?.nativeElement;
		const index: number = this.activeIndex();
		if (!resultsElement || index < 0) return;
		const activeItem: HTMLElement | null = resultsElement.querySelector(`[data-result-index="${index}"]`);
		if (!activeItem) return;
		activeItem.scrollIntoView({ block: 'nearest' });
	}
}
